<template>
  <teleport to="body">
    <div
      id="modalRoot"
      ref="modalRoot"
      :tabindex="-1"
      class="absolute right-0 left-0 top-0 bottom-0 z-50 flex items-start lg:items-center justify-center bg-neutral-900/20"
      @keyup.esc="close"
    >
      <div
        ref="modalBody"
        class="flex min-h-full min-w-full flex-col gap-6 rounded-lg bg-white p-6 lg:min-h-[175px] lg:min-w-[515px]"
        :class="optionalBodyClasses"
      >
        <div class="flex flex-col">
          <div class="flex items-center justify-between">
            <h2
              class="text-2xl font-bold leading-7 tracking-tight text-neutral-900"
              :class="{
                'font-bold': titleWeight === 'bold',
                'font-normal': titleWeight === 'normal'
              }"
            >
              {{ title }}
            </h2>
            <font-awesome-icon
              v-if="!disableClose"
              :icon="['fas', 'xmark']"
              size="1x"
              class="w-4 cursor-pointer text-neutral-800 hover:text-neutral-500 focus:text-neutral-700"
              @click="close"
            />
          </div>

          <div class="flex flex-col">
            <h3 v-if="description" class="text-xs font-normal text-neutral-600">{{ description }}</h3>
            <slot name="header" />
          </div>
        </div>

        <div class="flex-1">
          <slot />
        </div>

        <div v-if="$slots.actions" class="flex items-center justify-end gap-2">
          <slot name="actions" :close="close" />
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts" setup>
import { onClickOutside } from '@vueuse/core';
import { onMounted, ref } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const emit = defineEmits(['close']);
const modalBody = ref(null);

interface Props {
  title?: string;
  description?: string;
  clickOutside?: boolean;
  disableClose?: boolean;
  titleWeight?: 'bold' | 'normal';
  optionalBodyClasses?: string;
}

const props = withDefaults(defineProps<Props>(), {
  titleWeight: 'bold',
  title: '',
  description: '',
  optionalBodyClasses: ''
});

if (props.clickOutside) {
  onClickOutside(modalBody, (event) => close());
}

function close(): void {
  if (props.disableClose) {
    return;
  }

  emit('close');
}

// Note: Giving it focus makes the escape keyup event trigger without having to
//       first click on something in the modal.
const modalRoot = ref();
onMounted(() => {
  modalRoot.value?.focus();
});
</script>
